// src/context/NotificationContext.js
import React, {
  createContext,
  useReducer,
  useContext,
  useCallback,
  useEffect,
} from "react";
import config from "../config/config";
import {
  connectMessageSocket,
  disconnectMessageSocket,
} from "../websockets/ChatMessageSocketService";

// ——— Action types —————————————————————————————————————————————
const ADD_NOTIFICATION    = "ADD_NOTIFICATION";
const REMOVE_NOTIFICATION = "REMOVE_NOTIFICATION";

// ——— Reducer —————————————————————————————————————————————
function notificationReducer(state, action) {
  console.log("[NotificationProvider] reducer got action:", action);
  switch (action.type) {
    case ADD_NOTIFICATION:
      return [...state, action.payload];
    case REMOVE_NOTIFICATION:
      return state.filter((n) => n.id !== action.payload);
    default:
      return state;
  }
}

// ——— Context & Provider ——————————————————————————————————————
const NotificationContext = createContext();

export function NotificationProvider({ children }) {
    console.log("[NotificationProvider] mounted");
  const [notifications, dispatch] = useReducer(notificationReducer, []);

  // 📣 helper for components to fire a manual toast
  const notify = useCallback(
    ({ type = "info", message, duration = 5000 }) => {
      const id = Date.now().toString();
      console.debug("[NotificationProvider] notify() →", {
        id,
        type,
        message,
        duration,
      });
      dispatch({ type: ADD_NOTIFICATION, payload: { id, type, message } });
      setTimeout(() => {
        console.log("[NotificationProvider] auto-remove →", id);
        dispatch({ type: REMOVE_NOTIFICATION, payload: id });
      }, duration);
    },
    []
  );

  // 🔌 Single socket hookup for chat events
  useEffect(() => {
    console.log(
      "[NotificationProvider] connecting to chat message socket at",
      config.MESSAGE_SOCKET_URL
    );

    connectMessageSocket((eventType, msg) => {
      console.log(
        `[NotificationProvider] socket event received: ${eventType}`,
        msg
      );

      if (["CREATE", "UPDATE", "PATCH"].includes(eventType)) {
        notify({
          type: "info",
          message: `New message from ${msg.senderName || msg.sender || "someone"
            }`,
          duration: 8000,
        });
      }
    });

    return () => {
      console.debug("[NotificationProvider] disconnecting socket");
      disconnectMessageSocket();
    };
  }, [notify]);

  // 🗑️ optional manual removal helper
  const removeNotification = useCallback((id) => {
    console.debug("[NotificationProvider] removeNotification →", id);
    dispatch({ type: REMOVE_NOTIFICATION, payload: id });
  }, []);

  const sendVerificationEmail = useCallback(
    async ({ email, verificationLink }) => {
      const url = `${config.API_BASE_URL}/v1/notifications/send-verification-email`;
      console.debug("[NotificationProvider] sendVerificationEmail →", {
        url,
        email,
        verificationLink,
      });

      try {
        const res = await fetch(url, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ email, verificationLink }),
        });

        if (!res.ok) {
          const text = await res.text();
          console.error(
            "[NotificationProvider] sendVerificationEmail failed:",
            res.status,
            text
          );
          notify({
            type: "error",
            message:
              "Failed to send verification email. Please try again or contact support.",
          });
          return { ok: false, status: res.status, body: text };
        }

        notify({
          type: "success",
          message: "Verification email sent. Please check your inbox.",
        });

        return { ok: true };
      } catch (err) {
        console.error(
          "[NotificationProvider] sendVerificationEmail error:",
          err
        );
        notify({
          type: "error",
          message:
            "An error occurred while sending verification email. Please try again.",
        });
        return { ok: false, error: err };
      }
    },
    [notify]
  );

  useEffect(() => {
    console.log(
      "[NotificationProvider] notifications array now:",
      notifications
    );
  }, [notifications]);

  return (
    <NotificationContext.Provider
      value={{ notifications, notify, removeNotification, sendVerificationEmail }}
    >
      {children}
    </NotificationContext.Provider>
  );
}

// 🔍 Hook for consumers—**be sure to import this** where you need it!
export function useNotification() {
  const ctx = useContext(NotificationContext);
  if (!ctx) {
    throw new Error(
      "useNotification must be used within a NotificationProvider"
    );
  }
  return ctx;
}
